import React, { useState } from "react";
import { ModalProps } from "../../../Shared/types/ModalProps";
import { GenericButton } from "../../../Shared/components/GenericButton";
import {
  GenericInput,
  GenericMultipleFileInput,
  GenericTextareaInput,
} from "../../../Shared/components/GenericInput";
import { GenericSelectInput } from "../../../Shared/components/GenericSelectInput";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { CreateVehicleInput } from "../models/CreateVehicleInput";
import { VehicleService } from "../services/VehicleService";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { IVehicle } from "../models/Vehicle";
import { ICompany } from "../../Users/models/Company";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { vehicleTypeOptions } from "../data/vehicleType";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { AuthService } from "../../Auth/services/AuthService";
import { FileModel } from "../../../Shared/dto/create-file-input";
import { useTranslation } from "react-i18next";

type VehicleFormProps = ModalProps & {
  vehicle?: IVehicle;
  companies: ICompany[];
};

export const VehicleForm: React.FC<VehicleFormProps> = ({
  onClose,
  vehicle = null,
  companies = [],
}) => {
  const { t } = useTranslation()
  const companyOptions = companies.map((company) => ({
    label: company?.name ?? "Company",
    value: company?.id ?? "",
  }));

  const company = AuthService.getLocalUser()?.user?.companies?.[0];

  const message = "Submitting vehicle info, please wait...";
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();
  const [isLoading, setIsLoading] = useState(false);

  const [companyOption, setCompanyOption] = useState<string>(
    vehicle?.company?.id || ""
  );
  const [trailerNumber,setTrailerNumber] = useState<string>(vehicle?.truckNumber || "")
  const [companyOptionError, setCompanyOptionError] = useState("");

  const [name, setName] = useState(vehicle?.name || "");
  const [nameError, setNameError] = useState("");

  const [truckNumber, setTruckNumber] = useState(vehicle?.truckNumber || "");
  const [truckNumberError, setTruckNumberError] = useState("");

  const [chassisNumber, setChassisNumber] = useState(
    vehicle?.chassisNumber || ""
  );
  const [chassisNumberError, setChassisNumberError] = useState("");

  const [plateNumber, setPlateNumber] = useState(vehicle?.plateNumber || "");
  const [plateNumberError, setPlateNumberError] = useState("");

  const [vehicleType, setVehicleType] = useState(vehicle?.vehicleType || "");
  const [vehicleTypeError, setVehicleTypeError] = useState("");

  const [minimumWeight, setMinimumWeight] = useState(
    vehicle?.minimumWeight || ""
  );
  const [minimumWeightError, setMinimumWeightError] = useState("");

  const [maximumWeight, setMaximumWeight] = useState(
    vehicle?.maximumWeight || ""
  );
  const [maximumWeightError, setMaximumWeightError] = useState("");

  const [description, setDescription] = useState(vehicle?.description || "");
  const [descriptionError, setDescriptionError] = useState("");

  const [attachments, setAttachments] = useState<FileModel[]>(
    UtilService.attachmentsToFileModel(vehicle?.attachments) || []
  );
  const [attachmentError, setAttachmentError] = useState("");

  const onChangeName = (val: string) => {
    setName(val);
    setNameError(val.trim() === "" ? "Name is required" : "");
  };

  const onChangeTruckNumber = (val: string) => {
    setTruckNumber(val);
    setTruckNumberError(val.trim() === "" ? "Truck number is required" : "");
  };

  const onChangeChassisNumber = (val: string) => {
    setChassisNumber(val);
    setChassisNumberError(
      val.trim() === "" ? "Chassis number is required" : ""
    );
  };

  const onChangePlateNumber = (val: string) => {
    setPlateNumber(val);
    setPlateNumberError(val.trim() === "" ? "Plate number is required" : "");
  };

  const onChangeVehicleType = (val: string) => {
    setVehicleType(val);
    setVehicleTypeError(val === "" ? "Vehicle type is required" : "");
  };

  const onChangeMinimumWeight = (val: string) => {
    setMinimumWeight(val);
    setMinimumWeightError(
      val.trim() === "" ? "Minimum weight is required" : ""
    );
  };

  const onChangeMaximumWeight = (val: string) => {
    setMaximumWeight(val);
    setMaximumWeightError(
      val.trim() === "" ? "Maximum weight is required" : ""
    );
  };

  const onChangeDescription = (val: string) => {
    setDescription(val);
    setDescriptionError(val.trim() === "" ? "Description is required" : "");
  };
const onChangeTrailerNumber = (val: string) => {
    setTrailerNumber(val);
    // setTrailerNumberError(val.trim() === "" ? "Description is required" : "");
  };

  const onFileAttachments = async (files: File[]) => {
    let attachmentFiles = await Promise.all(
      files.map((file) => UtilService.onAttachFile(file, setAttachmentError))
    );
    if (attachmentFiles?.length > 0) {
      setAttachments(
        attachmentFiles.filter((file): file is FileModel => file !== undefined)
      );
    }
  };

  const resetForm = () => {
    setCompanyOption("");
    setCompanyOptionError("");

    setName("");
    setNameError("");

    setTruckNumber("");
    setTruckNumberError("");

    setChassisNumber("");
    setChassisNumberError("");

    setPlateNumber("");
    setPlateNumberError("");

    setVehicleType("");
    setVehicleTypeError("");

    setMinimumWeight("");
    setMinimumWeightError("");

    setMaximumWeight("");
    setMaximumWeightError("");

    setDescription("");
    setDescriptionError("");

    setAttachments([]);
    setAttachmentError("");
  };

  const isFormValid =
    name &&
    truckNumber &&
    chassisNumber &&
    plateNumber &&
    vehicleType &&
    minimumWeight &&
    maximumWeight &&
    description &&
    !nameError &&
    !truckNumberError &&
    !chassisNumberError &&
    !plateNumberError &&
    !vehicleTypeError &&
    !minimumWeightError &&
    !maximumWeightError &&
    !descriptionError &&
    !attachmentError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    let createVehicleInput = new CreateVehicleInput();
    if (vehicle?.id) {
      createVehicleInput.id = vehicle.id;
    }
    createVehicleInput.companyId =
      companyOption !== "" ? companyOption : company?.id;
    createVehicleInput.name = name.trim();
    createVehicleInput.truckNumber = truckNumber.trim();
    createVehicleInput.chassisNumber = chassisNumber.trim();
    createVehicleInput.plateNumber = plateNumber.trim();
    createVehicleInput.vehicleType = vehicleType;
    createVehicleInput.minimumWeight = minimumWeight.trim();
    createVehicleInput.maximumWeight = maximumWeight.trim();
    createVehicleInput.description = description.trim();
    createVehicleInput.attachments = attachments;

    try {
      await VehicleService.create(createVehicleInput);
      resetForm();
      onClose(true);
    } catch (error: any) {
      setToast({ message: `Error: ${error.message}`, type: "error" });
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  let yearOptions = []
  for (let i = (new Date()).getFullYear(); i >= 1980; i--) {
    yearOptions.push({ value: `${i}`, label: `${i}` })
  }

  const truckTypeOptions = [
    { label: t('truck_type_container'), value: t('truck_type_container') },
    { label: t('truck_type_tanker'), value: t('truck_type_tanker') },
    { label: t('truck_type_flatbed'), value: t('truck_type_flatbed') },
    { label: t('truck_type_refrigerator'), value: t('truck_type_refrigerator') },
    { label: t('truck_type_other'), value: t('truck_type_other') }]
  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg w-full max-w-lg p-5 mt-10 mb-10 max-h-screen overflow-y-auto">
          <div className="flex justify-between items-center pb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {vehicle?.id ? t('update_truck') : t('create_truck')}
            </h3>
            <button
              onClick={() => onClose(false)}
              className="text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {companies.length > 1 && (
              <GenericSelectInput
                label={t('company')}
                options={companyOptions}
                value={companyOption}
                onChange={(val) => {
                  setCompanyOption(val);
                  setCompanyOptionError(
                    val === "" ? "Company is required" : ""
                  );
                }}
                error={companyOptionError}
                placeholder={t('select_company')}
                required
              />
            )}

            <div className="grid gap-6 mb-6 md:grid-cols-2">
              <GenericInput
                containerClassName=""
                label={t('truck_make_label')}
                value={name}
                onChange={onChangeName}
                error={nameError}
                placeholder={t('truck_make_hint')}
                required
              />

              <GenericSelectInput
                containerClassName=""
                label={t('truck_year_label')}
                options={yearOptions}
                value={truckNumber}
                onChange={onChangeTruckNumber}
                error={truckNumberError}
                placeholder={t('truck_year_hint')}
                required
              />
            </div>

            <div className="grid gap-6 mb-6 md:grid-cols-2">
              <GenericInput
                containerClassName=""
                label={t('truck_chassis_label')}
                value={chassisNumber}
                onChange={onChangeChassisNumber}
                type="text"
                error={chassisNumberError}
                placeholder={t('truck_chassis_hint')}
                required
              />

              <GenericInput
                containerClassName=""
                label={t('truck_registration_label')}
                value={plateNumber}
                onChange={onChangePlateNumber}
                type="text"
                error={plateNumberError}
                placeholder={t('truck_registration_hint')}
                required
              />
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <GenericSelectInput
                label={t('truck_type_label')}
                options={truckTypeOptions}
                value={vehicleType}
                onChange={onChangeVehicleType}
                error={vehicleTypeError}
                placeholder={t('truck_type_hint')}
                required
              />
              <GenericInput
                label={t('truck_trailer_number_label')}
                value={trailerNumber}
                onChange={onChangeTrailerNumber}
                type="text"
                error={""}
                placeholder={t('truck_trailer_number_hint')}
                required
              />
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <GenericInput
                label={t('truck_min_weight_label')}
                value={minimumWeight}
                onChange={onChangeMinimumWeight}
                type="number"
                error={minimumWeightError}
                placeholder={t('truck_min_weight_hint')}
                required
              />
              <GenericInput
                label={t('truck_max_weight_label')}
                value={maximumWeight}
                onChange={onChangeMaximumWeight}
                type="number"
                error={maximumWeightError}
                placeholder={t('truck_max_weight_hint')}
                required
              />
            </div>

            <GenericTextareaInput
              label={t('truck_description_label')}
              value={description}
              onChange={onChangeDescription}
              error={descriptionError}
              placeholder={t('truck_description_hint')}
              required
            />

            <GenericMultipleFileInput
              label={t('truck_documents_label')}
              onChange={onFileAttachments}
              error={attachmentError}
              placeholder={t('truck_documents_hint')}
            />

            <div className="mt-4 text-center">
              <GenericButton type="submit" disabled={!isFormValid}>
                {vehicle?.id ? t('update_truck') : t('create_truck')}
              </GenericButton>
            </div>
          </form>
        </div>
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </>
  );
};
